// ======================================================================
// FILE: src/modules/wms/template/components/TemplateItemsTab.js
// MODULE: Work Management System (WMS)
// FEATURE: Template Management
//
// PURPOSE:
//   Manage Template Items
//
// DEPENDENCIES:
//   wmsTemplateService
//
// ARCHITECTURE:
//   ✓ TaskFlow ERP Standard
//   ✓ AdminLTE
//   ✓ Multi Tenant
//   ✓ Multi Location
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import wmsTemplateService
from "../../services/wmsTemplateService";

import TemplateItemChecklistsModal
from "./TemplateItemChecklistsModal";

const DEFAULT_FORM = {

  sequence_no: "",

  item_title: "",

  item_description: "",

  expected_response_type:
    "TEXT",

  mandatory: true,

  default_priority:
    "MEDIUM",

  estimated_minutes: "",

  remarks: "",
};

export default function TemplateItemsTab({
  templateId,
}) {

  const [loading, setLoading] =
    useState(false);

  const [rows, setRows] =
    useState([]);

  const [showModal, setShowModal] =
    useState(false);

  const [editingItem, setEditingItem] =
    useState(null);

  const [form, setForm] =
    useState(DEFAULT_FORM);

    const [
    showChecklistModal,
    setShowChecklistModal
    ] = useState(false);

    const [
    selectedItem,
    setSelectedItem
    ] = useState(null);    
  // ==========================================================
  // LOAD
  // ==========================================================

  useEffect(() => {

    if (templateId) {

      loadItems();
    }

  }, [templateId]);

  async function loadItems() {

    try {

      setLoading(true);

      const data =
        await wmsTemplateService
          .getTemplateItems(
            templateId
          );

      setRows(data || []);

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Failed to load items"
      );

    } finally {

      setLoading(false);
    }
  }

  // ==========================================================
  // ADD
  // ==========================================================

  function handleAdd() {

    setEditingItem(null);

    setForm(
      DEFAULT_FORM
    );

    setShowModal(true);
  }

  // ==========================================================
  // EDIT
  // ==========================================================

  function handleEdit(row) {

    setEditingItem(row);

    setForm({

      sequence_no:
        row.sequence_no || "",

      item_title:
        row.item_title || "",

      item_description:
        row.item_description || "",

      expected_response_type:
        row.expected_response_type
        || "TEXT",

      mandatory:
        row.mandatory ?? true,

      default_priority:
        row.default_priority
        || "MEDIUM",

      estimated_minutes:
        row.estimated_minutes
        || "",

      remarks:
        row.remarks || "",
    });

    setShowModal(true);
  }

  // ==========================================================
  // SAVE
  // ==========================================================

  async function handleSave() {

    if (!form.sequence_no) {

      alert(
        "Sequence No is required"
      );

      return;
    }

    if (!form.item_title) {

      alert(
        "Item Title is required"
      );

      return;
    }

    try {

      if (editingItem) {

        await wmsTemplateService
          .updateTemplateItem(
            templateId,
            editingItem.id,
            form
          );

      } else {

        await wmsTemplateService
          .createTemplateItem(
            templateId,
            form
          );
      }

      setShowModal(false);

      await loadItems();

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Save failed"
      );
    }
  }

  // ==========================================================
  // DELETE
  // ==========================================================

  async function handleDelete(row) {

    const ok = window.confirm(
      `Delete Item ${row.item_title}?`
    );

    if (!ok) {
      return;
    }

    try {

      await wmsTemplateService
        .deleteTemplateItem(
          templateId,
          row.id
        );

      await loadItems();

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Delete failed"
      );
    }
  }

  
  function handleManageChecklists(
  row
) {

  setSelectedItem(row);

  setShowChecklistModal(true);
}

  // ==========================================================
  // UI
  // ==========================================================

  return (

    <div className="card mt-3">

      <div
        className="
          card-header
          d-flex
          justify-content-between
          align-items-center
        "
      >

        <strong>
          Template Items
        </strong>

        <button
          className="btn btn-sm btn-primary"
          onClick={handleAdd}
        >
          <i className="fas fa-plus me-1" />
          Add Item
        </button>

      </div>

      <div className="card-body p-0">

        <table
          className="
            table
            table-striped
            table-hover
            mb-0
          "
        >

          <thead>

            <tr>

              <th>Seq</th>

              <th>Title</th>

              <th>Response Type</th>

              <th>Mandatory</th>

              <th>Priority</th>

              <th>Minutes</th>
              <th>
            Checklists
            </th>

              <th width="260">
                Actions
              </th>

            </tr>

          </thead>

          <tbody>

            {loading && (

              <tr>

                <td
                  colSpan="8"
                  className="text-center"
                >
                  Loading...
                </td>

              </tr>
            )}

            {!loading &&
              rows.length === 0 && (

              <tr>

                <td
                  colSpan="8"
                  className="text-center"
                >
                  No Records Found
                </td>

              </tr>
            )}

            {!loading &&
              rows.map((row) => (

              <tr key={row.id}>

                <td>
                  {row.sequence_no}
                </td>

                <td>
                  {row.item_title}
                </td>

                <td>
                  {row.expected_response_type}
                </td>

                <td>

                  {row.mandatory
                    ? "Yes"
                    : "No"}

                </td>

                <td>
                  {row.default_priority}
                </td>

                <td>
                    {row.estimated_minutes}
                    </td>

                    <td>

                    <button
                        className="
                        btn
                        btn-sm
                        btn-info
                        "
                        onClick={() =>
                        handleManageChecklists(
                            row
                        )
                        }
                    >
                        Checklists
                    </button>

                    </td>

                    <td>

  <button
                    className="
                      btn
                      btn-sm
                      btn-primary
                      me-1
                    "
                    onClick={() =>
                      handleEdit(row)
                    }
                  >
                    Edit
                  </button>

                  <button
                    className="
                      btn
                      btn-sm
                      btn-danger
                    "
                    onClick={() =>
                      handleDelete(row)
                    }
                  >
                    Delete
                  </button>

                </td>

              </tr>

            ))}

          </tbody>

        </table>

      </div>

      {/* ==================================================== */}
      {/* MODAL */}
      {/* ==================================================== */}

      {showModal && (

        <div
          className="modal d-block"
          tabIndex="-1"
        >

          <div className="modal-dialog modal-lg">

            <div className="modal-content">

              <div className="modal-header">

                <h5>

                  {editingItem
                    ? "Edit Item"
                    : "Add Item"}

                </h5>

              </div>

              <div className="modal-body">

                <div className="row">

                    <div className="col-md-3 mb-3">
                    <label>Sequence No *</label>
                    <input
                        type="number"
                        className="form-control"
                        value={form.sequence_no}
                        onChange={(e) =>
                        setForm({
                            ...form,
                            sequence_no: e.target.value,
                        })
                        }
                    />
                    </div>

                    <div className="col-md-9 mb-3">
                    <label>Title *</label>
                    <input
                        type="text"
                        className="form-control"
                        value={form.item_title}
                        onChange={(e) =>
                        setForm({
                            ...form,
                            item_title: e.target.value,
                        })
                        }
                    />
                    </div>

                    <div className="col-md-12 mb-3">
                    <label>Description</label>
                    <textarea
                        rows="3"
                        className="form-control"
                        value={form.item_description}
                        onChange={(e) =>
                        setForm({
                            ...form,
                            item_description: e.target.value,
                        })
                        }
                    />
                    </div>

                    <div className="col-md-3 mb-3">
                    <label>Response Type</label>
                    <select
                        className="form-control"
                        value={form.expected_response_type}
                        onChange={(e) =>
                        setForm({
                            ...form,
                            expected_response_type:
                            e.target.value,
                        })
                        }
                    >
                        <option value="TEXT">Text</option>
                        <option value="NUMBER">Number</option>
                        <option value="PASS_FAIL">Pass / Fail</option>
                        <option value="YES_NO">Yes / No</option>
                        <option value="DATE">Date</option>
                        <option value="DATETIME">Date Time</option>
                        <option value="PHOTO">Photo</option>
                    </select>
                    </div>

                    <div className="col-md-3 mb-3">
                    <label>Priority</label>
                    <select
                        className="form-control"
                        value={form.default_priority}
                        onChange={(e) =>
                        setForm({
                            ...form,
                            default_priority:
                            e.target.value,
                        })
                        }
                    >
                        <option value="LOW">Low</option>
                        <option value="MEDIUM">Medium</option>
                        <option value="HIGH">High</option>
                        <option value="CRITICAL">Critical</option>
                    </select>
                    </div>

                    <div className="col-md-3 mb-3">
                    <label>Estimated Minutes</label>
                    <input
                        type="number"
                        className="form-control"
                        value={form.estimated_minutes}
                        onChange={(e) =>
                        setForm({
                            ...form,
                            estimated_minutes:
                            e.target.value,
                        })
                        }
                    />
                    </div>

                    <div className="col-md-3 mb-3">

                    <label>&nbsp;</label>

                    <div className="form-check mt-2">

                        <input
                        type="checkbox"
                        className="form-check-input"
                        checked={form.mandatory}
                        onChange={(e) =>
                            setForm({
                            ...form,
                            mandatory:
                                e.target.checked,
                            })
                        }
                        />

                        <label className="form-check-label">
                        Mandatory
                        </label>

                    </div>

                    </div>

                    <div className="col-md-12 mb-3">

                    <label>Remarks</label>

                    <textarea
                        rows="2"
                        className="form-control"
                        value={form.remarks}
                        onChange={(e) =>
                        setForm({
                            ...form,
                            remarks: e.target.value,
                        })
                        }
                    />

                    </div>

                </div>

                </div>

              <div className="modal-footer">

                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                >
                  Save
                </button>

                <button
                  className="btn btn-secondary"
                  onClick={() =>
                    setShowModal(false)
                  }
                >
                  Cancel
                </button>

              </div>

            </div>

          </div>

        </div>

     
     
     )}

           <TemplateItemChecklistsModal

        show={
          showChecklistModal
        }

        onClose={() =>
          setShowChecklistModal(
            false
          )
        }

        templateItemId={
          selectedItem?.id
        }

        itemTitle={
          selectedItem?.item_title
        }

      />

    </div>
  );
}